// Right-panel mode for a selected edge in the workflow builder.
// Currently surfaces: the from/to node keys (read-only),
// the JSONata `condition` (read-only — editing still happens via the
// inline-on-edge label flow), and a BoardColumn picker (DEV-16183).
// Picking a column issues `PATCH /v1/workflows/:id/edges/:edgeId` which
// cuts a new version. Choosing the empty option clears the attribution.
import { useClient } from '@cortex/react-query';
import type { BoardColumn, IPaginated } from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useState, type CSSProperties } from 'react';

import { EmptyState } from '../../common/EmptyState';
import { Spinner } from '../../common/Spinner';
import { useWorkflowEditor } from '../WorkflowEditorContext';
import { resolveEdgeWiringChip, type IEdgeWiringData } from '../edge-wiring-chip';

const container: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-3)',
  padding: 'var(--sp-3)',
};

const fieldStack: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-2)',
};

const metaCard: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-2)',
  padding: 'var(--sp-3)',
  background: 'var(--surface-2)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-sm)',
};

export function EdgeInspectorPanel({
  edgeId,
  onRepin,
  onClose,
}: {
  edgeId: string | null;
  /**
   * Update the URL `edgeId` search param. Used to re-pin the inspector
   * to the *equivalent* edge after a PATCH cuts a new workflow version
   * (each version gets fresh cuid edge ids, so the row id we had no
   * longer exists — we look up the new one by `{source, target}` and
   * call this to keep the inspector on the same logical edge).
   */
  onRepin: (newEdgeId: string) => void;
  onClose: () => void;
}) {
  const editor = useWorkflowEditor();
  const client = useClient();
  const qc = useQueryClient();

  // Resolve the edge from the in-memory editor graph. Edges in xyflow
  // carry `id, source, target, data?.{condition, boardColumnId}` —
  // mirroring `WorkflowEdgeSchema`. (Stored alongside the persisted
  // version, sourced from the API on workflow load.)
  const edge = edgeId ? editor.edges.find((e) => e.id === edgeId) : undefined;

  // Remember the (source, target) of the last edge we successfully
  // resolved so we can find the "same" edge in a freshly-cut version,
  // whose row id will have changed. (DEV-16183.)
  const [lastFingerprint, setLastFingerprint] = useState<{
    source: string;
    target: string;
  } | null>(null);
  useEffect(() => {
    if (edge) {
      setLastFingerprint({ source: edge.source, target: edge.target });
    }
  }, [edge?.source, edge?.target]); // eslint-disable-line react-hooks/exhaustive-deps

  // When `edgeId` no longer resolves (post version-cut) but we still
  // know the fingerprint, search the current edges for the successor.
  // If found, re-pin the URL so the inspector stays on the same logical
  // edge instead of falling through to "Edge not found".
  useEffect(() => {
    if (edge || !edgeId || !lastFingerprint) return;
    const successor = editor.edges.find(
      (e) => e.source === lastFingerprint.source && e.target === lastFingerprint.target,
    );
    if (successor && successor.id !== edgeId) {
      onRepin(successor.id);
    }
  }, [edge, edgeId, editor.edges, lastFingerprint, onRepin]);

  const columnsQuery = useQuery({
    queryKey: ['board-columns'],
    queryFn: () => client.http.get('v1/board-columns?limit=500').json<IPaginated<BoardColumn>>(),
  });

  // Local optimistic copy of the edge's column so the <select> snaps to
  // the new value the moment the user picks it — without waiting for the
  // PATCH → version cut → query refetch → editor rehydration round trip.
  // Cleared back to undefined once we observe `edge.data.boardColumnId`
  // reflect the new value (or on inspector close). (DEV-16183.)
  const [pendingColumnId, setPendingColumnId] = useState<string | null | undefined>(undefined);

  const patch = useMutation({
    mutationFn: (boardColumnId: string | null) =>
      client.http
        .patch(`v1/workflows/${editor.workflow.id}/edges/${edgeId}`, {
          json: { boardColumnId },
        })
        .json<unknown>(),
    onMutate: (boardColumnId) => {
      setPendingColumnId(boardColumnId);
    },
    onError: () => {
      setPendingColumnId(undefined);
    },
    onSuccess: async () => {
      // Two cache keys must invalidate together:
      //   - `['workflow', id]` covers the workflow detail + the
      //     `['workflow', id, 'edges']` query (prefix match).
      //   - `['workflow-versions', id]` is owned by WorkflowEditorContext;
      //     missing it left `versions.data` stale, so the hydration
      //     `useEffect` could not resolve `currentVersionId` after the
      //     version cut and the canvas kept the old edges in xyflow
      //     state until a hard refresh.
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['workflow', editor.workflow.id] }),
        qc.invalidateQueries({ queryKey: ['workflow-versions', editor.workflow.id] }),
      ]);
    },
  });

  // Drop the optimistic copy once the server-side value catches up.
  // Hook must live above the early returns to keep React's rules-of-hooks
  // order stable across renders (the early returns below short-circuit
  // when the edge isn't resolvable).
  const serverColumnIdForReconcile =
    edge && (edge.data as { boardColumnId?: string | null } | undefined)?.boardColumnId
      ? ((edge.data as { boardColumnId?: string | null }).boardColumnId ?? null)
      : null;
  useEffect(() => {
    if (pendingColumnId !== undefined && pendingColumnId === serverColumnIdForReconcile) {
      setPendingColumnId(undefined);
    }
  }, [pendingColumnId, serverColumnIdForReconcile]);

  if (!edgeId) {
    return (
      <EmptyState
        title="No edge selected"
        description="Click an edge in the canvas to inspect it."
      />
    );
  }
  if (!edge) {
    return (
      <EmptyState
        title="Edge not found"
        description="The selected edge no longer exists. It may have been deleted."
      />
    );
  }

  const edgeData = (edge.data ?? {}) as {
    condition?: string | null;
    boardColumnId?: string | null;
  } & IEdgeWiringData;
  const serverColumnId = edgeData.boardColumnId ?? null;
  // Optimistic override: while the PATCH is in flight (and the editor's
  // edges haven't rehydrated yet) keep the user's selection visible in
  // the <select> so it doesn't snap back to the prior value. The
  // reconciliation effect that clears `pendingColumnId` lives above the
  // early returns.
  const effectiveColumnId = pendingColumnId !== undefined ? pendingColumnId : serverColumnId;
  const currentColumnId = effectiveColumnId ?? '';
  const columns = columnsQuery.data?.items ?? [];
  const wiringChip = resolveEdgeWiringChip(edgeData);

  return (
    <div style={container}>
      <div style={metaCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <code className="t-mono" style={{ fontSize: 'var(--fs-sm)' }}>
            EDGE · {edgeId.slice(0, 12)}
          </code>
          <button
            type="button"
            className="btn btn--ghost btn--xs"
            onClick={onClose}
            aria-label="Close edge inspector"
          >
            ✕
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--fs-xs)' }}>
          <span>
            <span className="t-mono-label">FROM</span> <code className="t-mono">{edge.source}</code>
          </span>
          <span>
            <span className="t-mono-label">TO</span> <code className="t-mono">{edge.target}</code>
          </span>
          {edgeData.condition ? (
            <span>
              <span className="t-mono-label">WHEN</span>{' '}
              <code className="t-mono">{edgeData.condition}</code>
            </span>
          ) : (
            <span className="t-secondary">No condition — fires unconditionally.</span>
          )}
        </div>
      </div>

      {wiringChip ? (
        <div
          className={
            wiringChip.tone === 'danger'
              ? 'alert alert--danger'
              : wiringChip.tone === 'warning'
                ? 'alert alert--warn'
                : 'alert alert--info'
          }
          style={{ fontSize: 12, margin: 0 }}
        >
          <strong>
            From {edge.source}: {wiringChip.label}
          </strong>
          <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{wiringChip.title}</div>
        </div>
      ) : null}

      <div style={fieldStack}>
        <label className="form-row">
          <span className="t-mono-label">BOARD COLUMN</span>
          <select
            className="input"
            value={currentColumnId}
            disabled={columnsQuery.isLoading || patch.isPending || editor.isReadOnly}
            onChange={(e) => {
              const next = e.target.value;
              patch.mutate(next === '' ? null : next);
            }}
          >
            <option value="">— None —</option>
            {columns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <span className="t-xs t-secondary">
            Runs that traverse this edge will land in the chosen column on the incident board while
            they&apos;re still in flight; once a run finishes it moves to Finished. Choosing
            &quot;None&quot; clears the attribution.{' '}
            <Link to="/board-columns" style={{ color: 'var(--text-secondary)' }}>
              Manage columns →
            </Link>
          </span>
        </label>
        {columnsQuery.isLoading ? <Spinner /> : null}
        {patch.isPending ? (
          <span className="t-xs t-secondary">Saving…</span>
        ) : patch.error ? (
          <div className="alert alert--danger" style={{ fontSize: 12 }}>
            {(patch.error as Error).message}
          </div>
        ) : null}
      </div>
    </div>
  );
}
